import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADDRESS, EMAIL, FARES, LEGAL, PHONE_NUMBER } from '../siteConfig';

// Privacy Policy, Terms & Conditions and Cookie Policy. The text describes
// what the site and booking system actually do (see the cookie audit in the
// Cookie Policy). Business facts come from LEGAL in siteConfig.js; anything
// not filled in yet renders as a highlighted marker so it can't be missed.
// Written in English; the owner should have it reviewed by a local lawyer
// and translated into Albanian before relying on it.

function Fill({ value, what }) {
  if (value) return <>{value}</>;
  return (
    <mark className="rounded bg-amber-100 px-1 font-semibold text-amber-900">[to be added: {what}]</mark>
  );
}

const Business = () => (
  <>
    <Fill value={LEGAL.businessName} what="registered business name" />, NIPT{' '}
    <Fill value={LEGAL.nipt} what="NIPT" />, registered at <Fill value={LEGAL.registeredAddress} what="registered address" />
  </>
);

const Contact = () => (
  <ul>
    <li>Email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a></li>
    <li>Phone and WhatsApp: {PHONE_NUMBER}</li>
    <li>Address: {ADDRESS}</li>
  </ul>
);

function LegalLayout({ title, children }) {
  const { t, i18n } = useTranslation();
  return (
    <main id="main" className="bg-white font-sans text-navy">
      <section className="bg-navy py-14 text-white lg:py-20">
        <div className="site-container">
          <h1 className="m-0 text-[32px] font-extrabold tracking-tight lg:text-[48px]">{title}</h1>
          <p className="mb-0 mt-3 text-slate-300">Last updated: {LEGAL.lastUpdated}</p>
        </div>
      </section>
      <div className="site-container py-10 lg:py-14">
        {i18n.resolvedLanguage !== 'en' && (
          <p className="m-0 mb-8 max-w-[70ch] rounded-field bg-mist px-4 py-3 text-sm text-slate-700">{t('site.legal.englishOnly')}</p>
        )}
        <article className="legal max-w-[70ch]">{children}</article>
        <p className="mt-12 max-w-[70ch] border-0 border-t border-solid border-slate-200 pt-6 text-sm text-slate-600">
          See also: <Link to="/privacy">Privacy Policy</Link> · <Link to="/terms">Terms &amp; Conditions</Link> ·{' '}
          <Link to="/cookies">Cookie Policy</Link>
        </p>
      </div>
    </main>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        This policy explains what personal data TaxiReady2Go collects when you book a ride or contact us, why we
        collect it, who sees it and what rights you have. It follows Albanian Law No. 124/2024 "On Personal Data
        Protection" and, for visitors from the European Union, the EU General Data Protection Regulation (GDPR).
      </p>

      <h2>1. Who we are</h2>
      <p>
        TaxiReady2Go (<Business />) is responsible for your personal data (the "controller"). You can reach us about
        anything in this policy at:
      </p>
      <Contact />

      <h2>2. What we collect</h2>
      <h3>When you book</h3>
      <ul>
        <li>Your name, phone number and email address</li>
        <li>Pickup and drop-off addresses, date and time of the ride, number of passengers</li>
        <li>Anything you write in the booking notes</li>
      </ul>
      <h3>When you contact us</h3>
      <p>Messages, emails and calls you send us, including by WhatsApp.</p>
      <h3>When you visit the website</h3>
      <p>
        Our server keeps standard technical logs (IP address, browser type, the page requested and the time) so we
        can keep the site running and secure. The website remembers your chosen language in your browser; see the{' '}
        <Link to="/cookies">Cookie Policy</Link>. We do not use analytics, advertising or tracking tools.
      </p>
      <p>We do not take card payments on the website and never see your card details online.</p>

      <h2>3. Why we use it and our legal basis</h2>
      <ul>
        <li>
          <strong>To take and carry out your booking</strong>: assigning a driver, sending your booking emails
          (request received, confirmed, driver on the way, completed or cancelled) and reminding your driver before
          pickup. Legal basis: performing our contract with you.
        </li>
        <li>
          <strong>To answer your questions and complaints.</strong> Legal basis: performing our contract with you,
          or our legitimate interest in helping you.
        </li>
        <li>
          <strong>To keep the website and booking system secure</strong>, for example limiting repeated login
          attempts and preventing fake bookings. Legal basis: our legitimate interest in protecting our service.
        </li>
        <li>
          <strong>To meet legal obligations</strong>, such as tax and accounting records. Legal basis: legal
          obligation.
        </li>
      </ul>
      <p>
        We do not send marketing messages unless you have agreed to receive them, and we do not make decisions about
        you by automated means alone.
      </p>

      <h2>4. Who sees your data</h2>
      <ul>
        <li>
          <strong>Our drivers.</strong> To arrange your ride, booking details (your name, phone number, pickup,
          drop-off and time) are shared with our drivers, including through a WhatsApp group used for dispatch.
        </li>
        <li>
          <strong>Service providers who work for us:</strong> Hetzner Online GmbH hosts our website and booking
          database (servers in <Fill value={LEGAL.hostingLocation} what="server location" />); Google (Gmail) sends
          our booking emails; WhatsApp (Meta) carries messages between us and our drivers, and with you if you
          message us.
        </li>
        <li><strong>Authorities</strong>, when the law requires us to share information.</li>
      </ul>
      <p>We never sell your personal data.</p>

      <h2>5. Transfers outside Albania</h2>
      <p>
        Some of these providers, such as Google and Meta, may process data outside Albania and the European Economic
        Area, including in the United States. Where that happens we rely on the safeguards these providers offer
        under data protection law, such as standard contractual clauses or recognised certification frameworks.
      </p>

      <h2>6. How long we keep it</h2>
      <ul>
        <li>Booking records: <Fill value={LEGAL.bookingRetention} what="how long booking records are kept" /></li>
        <li>Server logs: <Fill value={LEGAL.logRetention} what="how long server logs are kept" /></li>
        <li>Messages and emails: as long as needed to deal with your request, then deleted.</li>
      </ul>

      <h2>7. How we protect it</h2>
      <p>
        The website uses encrypted connections (HTTPS). Only our staff and the driver handling your ride can see your
        booking; staff accounts are protected by passwords stored in hashed form, and repeated login attempts are
        blocked. Your booking status page can only be opened with the private link in your email.
      </p>

      <h2>8. Your rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li>ask for a copy of the personal data we hold about you</li>
        <li>have incorrect data corrected</li>
        <li>have your data deleted, unless we must keep it by law</li>
        <li>restrict or object to how we use it</li>
        <li>receive your data in a portable format</li>
        <li>withdraw any consent you gave, at any time</li>
      </ul>
      <p>
        Email us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a> to use any of these rights. We will reply within one
        month. You can also complain to the Commissioner for the Right to Information and Personal Data Protection
        (<a href="https://idp.al" target="_blank" rel="noopener noreferrer">idp.al</a>), or, if you live in the EU,
        to the data protection authority of your country.
      </p>

      <h2>9. Children</h2>
      <p>
        Bookings must be made by an adult. Children are welcome to travel with an adult who books for them.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        If we change this policy, we will publish the new version on this page with a new "Last updated" date.
      </p>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout title="Terms & Conditions">
      <p>
        These terms apply when you book a ride with TaxiReady2Go through our website, by phone or by WhatsApp. By
        booking, you agree to them. They do not affect your rights as a consumer under Albanian law.
      </p>

      <h2>1. Who we are</h2>
      <p>
        TaxiReady2Go (<Business />). Contact us at:
      </p>
      <Contact />

      <h2>2. Our service</h2>
      <p>
        We provide taxi and transfer rides in and around Tirana, including transfers to and from Tirana International
        Airport (TIA), city rides and hourly hire.
      </p>

      <h2>3. Bookings</h2>
      <ul>
        <li>
          When you book online you receive a "request received" email. Your booking is confirmed when we assign a
          driver and send you a "ride confirmed" email.
        </li>
        <li>
          If we cannot carry out a requested ride (for example, no driver is available at that time), we will tell
          you as soon as possible and you will not be charged.
        </li>
        <li>
          Please give correct details, especially your phone number, pickup address and time, and contact us
          straight away if anything changes.
        </li>
      </ul>

      <h2>4. Fares</h2>
      <ul>
        <li>
          Fixed fares apply between Tirana International Airport and Tirana city centre: €{FARES.toCity} from the
          airport to the city centre and €{FARES.toAirport} from the city centre to the airport. Current fares are
          shown on our <Link to="/rates">Rates</Link> page.
        </li>
        <li>For any other trip, we tell you the price before the ride.</li>
        <li>The price we confirm is the price you pay, unless you change the trip (for example, an extra stop).</li>
      </ul>

      <h2>5. Payment</h2>
      <p>
        You pay the driver at the end of the ride. Accepted methods:{' '}
        <Fill value={LEGAL.paymentMethods} what="accepted payment methods" />.
      </p>

      <h2>6. Pickup and waiting time</h2>
      <ul>
        <li>For airport pickups, your driver meets you at arrivals with a sign showing your name.</li>
        <li>
          Waiting time included: <Fill value={LEGAL.includedWaiting} what="included waiting time" />. If your
          flight is delayed, please let us know by phone or WhatsApp.
        </li>
        <li>For other pickups, please be ready at the agreed place and time.</li>
      </ul>

      <h2>7. Changes and cancellations</h2>
      <ul>
        <li>To change or cancel a booking, contact us by phone, WhatsApp or email as early as possible.</li>
        <li>
          Free cancellation: <Fill value={LEGAL.freeCancellation} what="free cancellation period" />. Later
          cancellations or not showing up:{' '}
          <Fill value={LEGAL.lateCancellationFee} what="late cancellation / no-show fee" />.
        </li>
        <li>
          If we have to cancel your ride, we will tell you by email and help you find another option. You will not
          be charged.
        </li>
      </ul>

      <h2>8. Passengers and luggage</h2>
      <ul>
        <li>Maximum passengers: <Fill value={LEGAL.maxPassengers} what="maximum passengers per car" />. Tell us in advance about large luggage, child seats or pets.</li>
        <li>All passengers must wear seatbelts. Smoking is not allowed in our cars.</li>
        <li>
          Our drivers may refuse or end a ride if a passenger behaves in a way that is unsafe, abusive or illegal.
        </li>
      </ul>

      <h2>9. Our responsibility</h2>
      <ul>
        <li>We carry out every ride with reasonable care and skill, with the insurance required by Albanian law.</li>
        <li>
          We are not responsible for delays caused by things outside our control, such as traffic, road closures,
          weather or flight delays.
        </li>
        <li>
          Nothing in these terms limits our responsibility where the law does not allow it to be limited, including
          for death or personal injury caused by our negligence, or your rights as a consumer.
        </li>
      </ul>

      <h2>10. Complaints</h2>
      <p>
        If something went wrong, contact us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a> with your booking number. We
        will reply within {LEGAL.complaintReplyDays} days. If you are not satisfied with our answer, you can contact
        the consumer protection authorities in Albania.
      </p>

      <h2>11. Law and courts</h2>
      <p>
        These terms are governed by Albanian law, and disputes are handled by the competent courts in Tirana. If
        you are a consumer living in another country, you keep the protection of the mandatory consumer laws of
        that country.
      </p>

      <h2>12. Changes to these terms</h2>
      <p>
        We may update these terms. The version on this page when you book applies to your booking.
      </p>
    </LegalLayout>
  );
}

export function CookiePolicy() {
  return (
    <LegalLayout title="Cookie Policy">
      <p>
        <strong>Short version: our website does not use cookies</strong>, and it does not use any analytics,
        advertising or tracking tools. That is why you don't see a cookie banner.
      </p>

      <h2>1. What we store in your browser</h2>
      <p>
        The website uses your browser's local storage, which works like a cookie but is never sent to our server,
        for one thing only:
      </p>
      <div className="legal-table">
        <table>
          <thead>
            <tr><th>Name</th><th>What it does</th><th>How long</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><code>i18nextLng</code></td>
              <td>Remembers the language you chose, so the website stays in that language.</td>
              <td>Until you clear your browser data</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        This is needed to give you the language you asked for, so it does not require consent. Our staff and drivers
        also stay logged in through local storage on their own staff pages; this is never used for customers.
      </p>

      <h2>2. Other websites</h2>
      <p>
        When you open our website, your browser does not contact any other company: our fonts, images and code all
        come from our own server. If you tap the WhatsApp, phone or email links, the app you open (for example
        WhatsApp, run by Meta) handles your data under its own privacy policy.
      </p>

      <h2>3. Clearing it</h2>
      <p>
        You can delete the stored language choice at any time by clearing your browser's site data for our website.
        The website will then show in your browser's language again.
      </p>

      <h2>4. Changes</h2>
      <p>
        If we ever add analytics or other tools that use cookies, we will ask for your consent first and update this
        page.
      </p>

      <h2>5. Contact</h2>
      <p>
        Questions about this policy: <a href={`mailto:${EMAIL}`}>{EMAIL}</a>. See also our{' '}
        <Link to="/privacy">Privacy Policy</Link>.
      </p>
    </LegalLayout>
  );
}
